import * as React from 'react';
import { useState } from 'react';
import { AvatarGroup, Chip, Paper, Typography, Box } from '@mui/material';
import DealIcon from '@mui/icons-material/MonetizationOn';
import { format } from 'date-fns';
import {
    useCreatePath,
    useRecordContext,
    Link,
    ReferenceManyField,
    useListContext,
} from 'react-admin';

import { Contact, Supplier } from '../types';
import { Avatar } from '../suppliercontacts/Avatar';

const chipSx = {
    height: 18,
    fontSize: '0.65rem',
    '& .MuiChip-label': { px: 0.75 },
};

const formatAnalise = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return format(date, 'dd/MM/yyyy');
};

export const SupplierCard = (props: { record?: Supplier }) => {
    const [elevation, setElevation] = useState(1);
    const createPath = useCreatePath();
    const record = useRecordContext<Supplier>(props);
    if (!record) return null;

    const cfg = record.config_fornec;
    const analise = formatAnalise(record.dthr_analise);
    const diaAnalise = [record.dia_semana, analise].filter(Boolean).join(' · ');

    return (
        <Link
            to={createPath({
                resource: 'suppliers',
                id: record.id,
                type: 'show',
            })}
            underline="none"
            onMouseEnter={() => setElevation(3)}
            onMouseLeave={() => setElevation(1)}
        >
            <Paper
                sx={{
                    height: 228,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: 1.25,
                    overflow: 'hidden',
                }}
                elevation={elevation}
            >
                <Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={0.5}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {record.id}
                        </Typography>
                        <Box display="flex" gap={0.5}>
                            {cfg?.semana_par ? (
                                <Chip label="Par" size="small" sx={chipSx} />
                            ) : null}
                            {cfg?.semana_impar ? (
                                <Chip label="Ímpar" size="small" sx={chipSx} />
                            ) : null}
                        </Box>
                    </Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mt: 0.5,
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {record.nome_fornec}
                    </Typography>
                    {record.id_pessoa != null ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                        >
                            Pessoa {record.id_pessoa}
                        </Typography>
                    ) : null}
                    {record.nome_comprador ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                        >
                            {record.nome_comprador}
                        </Typography>
                    ) : null}
                    {diaAnalise ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                        >
                            {diaAnalise}
                        </Typography>
                    ) : null}
                </Box>
                <Box
                    display="flex"
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0.5}
                    minHeight={24}
                >
                    <Box display="flex" alignItems="center" minWidth={0}>
                        {record.nb_contacts ? (
                            <ReferenceManyField
                                reference="suppliercontacts"
                                source="id_pessoa"
                                target="id_pessoa"
                            >
                                <AvatarGroupIterator />
                            </ReferenceManyField>
                        ) : null}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                        {record.quant_skus != null ? (
                            <Typography variant="caption" color="text.secondary">
                                {record.quant_skus.toLocaleString('pt-BR')} SKUs
                            </Typography>
                        ) : null}
                        {record.nb_orders ? (
                            <Box display="flex" alignItems="center" gap={0.25}>
                                <DealIcon
                                    color="disabled"
                                    sx={{ fontSize: 16 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {record.nb_orders}{' '}
                                    {record.nb_orders > 1 ? 'OCs' : 'OC'}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Paper>
        </Link>
    );
};

const AvatarGroupIterator = () => {
    const { data, total, error, isPending } = useListContext<Contact>();
    if (isPending || error) return null;
    return (
        <AvatarGroup
            max={4}
            total={total}
            spacing="medium"
            sx={{
                '& .MuiAvatar-circular': {
                    width: 20,
                    height: 20,
                    fontSize: '0.6rem',
                },
            }}
        >
            {data.map((contact: Contact) => (
                <Avatar
                    key={contact.id}
                    record={contact}
                    width={20}
                    height={20}
                    title={`${contact.nome_contato}`}
                />
            ))}
        </AvatarGroup>
    );
};
